import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReportLibrary = ({ categories = [], onGenerateReport }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name'); // name, lastGenerated, size

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatFileSize = (sizeString) => {
    return sizeString;
  };

  const getFilteredReports = () => {
    let allReports = [];
    
    categories?.forEach(category => {
      category?.reports?.forEach(report => {
        allReports?.push({ ...report, categoryId: category?.id, categoryTitle: category?.title });
      });
    });

    // Filter by category
    if (selectedCategory !== 'all') {
      allReports = allReports?.filter(report => report?.categoryId === selectedCategory);
    }

    // Filter by search term
    if (searchTerm) {
      allReports = allReports?.filter(report => 
        report?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        report?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    // Sort reports
    allReports?.sort((a, b) => {
      switch (sortBy) {
        case 'lastGenerated':
          return new Date(b.lastGenerated) - new Date(a.lastGenerated);
        case 'size':
          const aSize = parseFloat(a?.size);
          const bSize = parseFloat(b?.size);
          return bSize - aSize;
        default:
          return a?.name?.localeCompare(b?.name);
      }
    });

    return allReports;
  };

  const filteredReports = getFilteredReports();

  const handlePreviewReport = (reportId) => {
    console.log(`Previewing report: ${reportId}`);
    // Open preview modal
  };

  const handleDownloadReport = (reportId) => {
    console.log(`Downloading report: ${reportId}`);
    // Implement download functionality
  };

  const handleCustomizeReport = (reportId) => {
    console.log(`Customizing report: ${reportId}`);
    onGenerateReport?.(reportId);
  };

  return (
    <div className="space-y-6">
      {/* Search and Filter Bar */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div className="flex items-center space-x-4">
          <div className="relative flex-1 md:w-96">
            <Icon name="Search" size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">All Categories</option>
            {categories?.map((category) => (
              <option key={category?.id} value={category?.id}>
                {category?.title} ({category?.count})
              </option>
            ))}
          </select>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="name">Sort by Name</option>
            <option value="lastGenerated">Sort by Date</option>
            <option value="size">Sort by Size</option>
          </select>
        </div>
      </div>
      {/* Category Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {categories?.map((category) => (
          <div key={category?.id} className="bg-card border border-border rounded-lg p-6 card-shadow hover:shadow-lg transition-smooth">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name={category?.icon} size={24} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">{category?.title}</h3>
                <p className="text-sm text-muted-foreground">{category?.count} reports</p>
              </div>
            </div>
            <div className="space-y-2">
              {category?.reports?.slice(0, 2)?.map((report) => (
                <div key={report?.id} className="flex items-center justify-between text-sm">
                  <span className="text-foreground">{report?.name}</span>
                  <span className="text-muted-foreground">{formatDate(report?.lastGenerated)}</span>
                </div>
              ))}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full mt-4"
              onClick={() => setSelectedCategory(category?.id)}
            >
              View All {category?.count} Reports
            </Button>
          </div>
        ))}
      </div>
      {/* Reports Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">
            {selectedCategory === 'all' ? 'All Reports' : categories?.find(c => c?.id === selectedCategory)?.title}
            <span className="text-muted-foreground ml-2">({filteredReports?.length} reports)</span>
          </h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredReports?.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Icon name="FileX" size={48} className="text-muted-foreground mx-auto mb-4" />
              <h4 className="text-lg font-medium text-foreground mb-2">No Reports Found</h4>
              <p className="text-muted-foreground">
                {searchTerm ? 'Try adjusting your search terms' : 'No reports available in this category'}
              </p>
            </div>
          ) : (
            filteredReports?.map((report) => (
              <div key={report?.id} className="bg-card border border-border rounded-lg p-6 card-shadow hover:shadow-lg transition-smooth group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="FileText" size={20} className="text-muted-foreground" />
                    </div>
                    <div>
                      <span className="text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                        {report?.categoryTitle}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="sm" onClick={() => handlePreviewReport(report?.id)}>
                      <Icon name="Eye" size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDownloadReport(report?.id)}>
                      <Icon name="Download" size={14} />
                    </Button>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground mb-1">{report?.name}</h4>
                    <p className="text-sm text-muted-foreground">{report?.description}</p>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>Updated {formatDate(report?.lastGenerated)}</span>
                    <span>{formatFileSize(report?.size)}</span>
                  </div>
                </div>
                
                <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-border">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    iconName="Download"
                    className="flex-1"
                    onClick={() => handleDownloadReport(report?.id)}
                  >
                    Download
                  </Button>
                  <Button 
                    variant="default" 
                    size="sm" 
                    iconName="Settings"
                    className="flex-1"
                    onClick={() => handleCustomizeReport(report?.id)}
                  >
                    Generate
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportLibrary;